// Diagnostic Attempts table for the BMD report. Lists every
// `dt_diagnostics_session_topic` for the selected student and year level, in
// reverse-chronological order. Score renders as filled and empty circles plus
// an "N/M" caption. The status pill mapping is simplified: once the
// remediation flow lands, COMPLETED can split into FM and chain pending, and
// REMEDIATED can pick up "SB done".
//
// The session dropdown above the table filters rows client-side to a single
// `fk_diagnostics_session_key`, or "all". KPIs and the radar are deliberately
// not scoped by this filter; they always reflect latest-per-topic across the
// year level.

import React from "react"
import type { BmdAttemptHistoryRow } from "../types/bmd-report.types"
import { plainText } from "../utils/formatters"

const ALL_DATES = "__all__"

type DateOption = {
  // Plain date label, also used as the dropdown value (dates are unique).
  date: string
  ts: number
}

// Per-session metadata: which date it belongs to, plus, when that date holds
// more than one test, its 1-based ordinal (newest is Test 1) and the total for
// that date. The row reads this to render the small "Test N" pill that
// separates same-date sessions.
type SessionMeta = {
  dateLabel: string
  testIndex: number
  testTotal: number
}

function buildSessionIndex(attempts: BmdAttemptHistoryRow[]): {
  dateOptions: DateOption[]
  bySessionKey: Map<string, SessionMeta>
} {
  const latestByKey = new Map<string, number>()
  for (const a of attempts) {
    const key = a.fk_diagnostics_session_key
    if (!key) continue
    const t = a.attempted_on ? new Date(a.attempted_on).getTime() : NaN
    if (Number.isNaN(t)) continue
    const prev = latestByKey.get(key)
    if (prev == null || t > prev) latestByKey.set(key, t)
  }

  const formatDate = (ts: number) =>
    new Date(ts).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })

  type Bucket = { date: string; maxTs: number; entries: { key: string; ts: number }[] }
  const buckets = new Map<string, Bucket>()
  for (const [key, ts] of latestByKey.entries()) {
    const date = formatDate(ts)
    let b = buckets.get(date)
    if (!b) {
      b = { date, maxTs: ts, entries: [] }
      buckets.set(date, b)
    }
    b.entries.push({ key, ts })
    if (ts > b.maxTs) b.maxTs = ts
  }

  const dateOptions: DateOption[] = Array.from(buckets.values())
    .sort((a, b) => b.maxTs - a.maxTs)
    .map((b) => ({ date: b.date, ts: b.maxTs }))

  const bySessionKey = new Map<string, SessionMeta>()
  for (const b of buckets.values()) {
    b.entries.sort((a, c) => c.ts - a.ts)
    const total = b.entries.length
    b.entries.forEach((e, i) => {
      bySessionKey.set(e.key, {
        dateLabel: b.date,
        testIndex: i + 1,
        testTotal: total,
      })
    })
  }

  return { dateOptions, bySessionKey }
}

type StatusPill = {
  label: string
  bgClass: string
  textClass: string
}

// Splits the "pending remediation" pill into what the topic is actually
// waiting for. Set this to false and every topic goes back to the single
// "Pending remediation" pill, with no other edit needed. The two backend
// counts it reads are additive and can stay either way.
const SPLIT_PENDING_PILL = true

// Returns every status that applies, not just the most severe. A raised flag
// used to short-circuit and hide the topic's own state, so a topic that was
// both flagged and awaiting remediation showed only the flag and the tutor
// could not see there was outstanding work as well.
function statusPillsFor(row: BmdAttemptHistoryRow): StatusPill[] {
  const pills: StatusPill[] = []
  if (row.has_unresolved_flag) {
    pills.push({
      label: "Intervention needed",
      bgClass: "bg-danger",
      textClass: "text-white",
    })
  }
  switch (row.topic_status) {
    case "REMEDIATED":
      pills.push({
        label: "Passed",
        bgClass: "bg-success",
        textClass: "text-white",
      })
      break
    case "COMPLETED": {
      const fm = row.pending_fm_count ?? 0
      const chain = row.chain_owed_count ?? 0
      // Both can be outstanding at once: a fundamental misconception earns the
      // FM retry and a prerequisite chain.
      const label =
        !SPLIT_PENDING_PILL || (fm === 0 && chain === 0)
          ? "Pending remediation"
          : fm > 0 && chain > 0
            ? "Pending FM + prerequisite test"
            : fm > 0
              ? "Pending FM"
              : "Pending prerequisite test"
      pills.push({
        label,
        bgClass: "bg-warning",
        textClass: "text-dark",
      })
      break
    }
    case "IN_PROGRESS":
      pills.push({
        label: "In progress",
        bgClass: "bg-info",
        textClass: "text-white",
      })
      break
    case "PENDING":
    default:
      pills.push({
        label: "Pending",
        bgClass: "bg-secondary",
        textClass: "text-white",
      })
      break
  }
  return pills
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

const ScoreCircles: React.FC<{
  correct: number
  total: number
}> = ({ correct, total }) => {
  if (total <= 0) {
    return <span className="text-muted">—</span>
  }
  // Cap visible circles to keep the row from breaking on wide topics. The
  // text "N/M" is the source of truth.
  const cap = 12
  const visible = Math.min(total, cap)
  const correctVisible = Math.min(correct, visible)
  const circles = []
  for (let i = 0; i < visible; i++) {
    const filled = i < correctVisible
    circles.push(
      <span
        key={i}
        className="d-inline-block rounded-circle me-1"
        style={{
          width: 10,
          height: 10,
          backgroundColor: filled ? "#198754" : "#dee2e6",
        }}
      />,
    )
  }
  // The PERCENTAGE leads, because that is the number a tutor compares across
  // topics: "1/15" and "3/44" are not comparable at a glance, 7% and 7% are.
  // The fraction stays underneath as the raw count, and the dots stay because
  // they show WHICH questions went wrong at a glance.
  const pct = Math.round((correct / total) * 100)
  // Traffic-light on the pass mark used everywhere else in the feature.
  const tone = pct >= 75 ? "#15803d" : pct >= 50 ? "#92400e" : "#b42318"
  // Dots first, then the score - the original left-to-right order. Only the
  // score itself changed: it leads with the percentage now, because "1/15" and
  // "3/44" are not comparable at a glance whereas 7% and 7% are.
  return (
    <div className="d-flex align-items-center gap-2">
      <span className="d-inline-flex align-items-center">{circles}</span>
      <div className="text-nowrap">
        <div className="fw-bold" style={{ fontSize: "20px", color: tone }}>
          {pct}%
        </div>
        <div className="text-muted font-size-12">
          {correct}/{total}
        </div>
      </div>
    </div>
  )
}

export type BmdAttemptHistoryTableProps = {
  attempts: BmdAttemptHistoryRow[]
  onPreview?: (row: BmdAttemptHistoryRow) => void
  // When provided, the per-row "Chain" button and the header link become
  // active and hand off to the Chain Analysis tab. The row variant carries
  // topic context; the header variant opens the tab unscoped.
  onViewChainAnalysis?: (row: BmdAttemptHistoryRow | null) => void
}

export const BmdAttemptHistoryTable: React.FC<BmdAttemptHistoryTableProps> = ({
  attempts,
  onPreview,
  onViewChainAnalysis,
}) => {
  const { dateOptions, bySessionKey } = React.useMemo(
    () => buildSessionIndex(attempts),
    [attempts],
  )
  const [selectedDate, setSelectedDate] = React.useState<string>(ALL_DATES)
  // 0 means "all tests on this date", otherwise it is the 1-based test index.
  // Only surfaces when the selected date has more than one test.
  const [selectedTestIndex, setSelectedTestIndex] = React.useState<number>(0)

  // If the selected date disappears on a year-level change, drop back to
  // "all" so the table does not go silently empty. Reset the test selector
  // whenever the date changes.
  React.useEffect(() => {
    if (
      selectedDate !== ALL_DATES &&
      !dateOptions.some((d) => d.date === selectedDate)
    ) {
      setSelectedDate(ALL_DATES)
    }
  }, [dateOptions, selectedDate])

  React.useEffect(() => {
    setSelectedTestIndex(0)
  }, [selectedDate])

  // How many tests live under the currently-selected date. Drives whether
  // the secondary "Test" dropdown appears.
  const testsForSelectedDate = React.useMemo(() => {
    if (selectedDate === ALL_DATES) return 0
    let n = 0
    for (const meta of bySessionKey.values()) {
      if (meta.dateLabel === selectedDate) n = Math.max(n, meta.testTotal)
    }
    return n
  }, [bySessionKey, selectedDate])

  const visible = attempts.filter((a) => {
    const meta = bySessionKey.get(a.fk_diagnostics_session_key)
    if (selectedDate !== ALL_DATES && meta?.dateLabel !== selectedDate) {
      return false
    }
    if (
      selectedDate !== ALL_DATES &&
      selectedTestIndex !== 0 &&
      meta?.testIndex !== selectedTestIndex
    ) {
      return false
    }
    return true
  })

  return (
    <div className="bg-white rounded shadow-sm p-4 mb-4">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div className="text-uppercase text-muted font-size-12">
            Diagnostic Attempts
          </div>
          {dateOptions.length > 0 && (
            <div className="d-flex align-items-center">
              <label
                className="text-muted font-size-12 me-2 mb-0"
                htmlFor="bmd-session-filter"
              >
                Session:
              </label>
              <select
                id="bmd-session-filter"
                className="form-select form-select-sm"
                style={{ minWidth: 160 }}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              >
                <option value={ALL_DATES}>All sessions</option>
                {dateOptions.map((d) => (
                  <option key={d.date} value={d.date}>
                    {d.date}
                  </option>
                ))}
              </select>
            </div>
          )}
          {testsForSelectedDate > 1 && (
            <div className="d-flex align-items-center">
              <label
                className="text-muted font-size-12 me-2 mb-0"
                htmlFor="bmd-test-filter"
              >
                Test:
              </label>
              <select
                id="bmd-test-filter"
                className="form-select form-select-sm"
                style={{ minWidth: 110 }}
                value={String(selectedTestIndex)}
                onChange={(e) =>
                  setSelectedTestIndex(Number(e.target.value) || 0)
                }
              >
                <option value="0">All</option>
                {Array.from(
                  { length: testsForSelectedDate },
                  (_, i) => i + 1,
                ).map((idx) => (
                  <option key={idx} value={idx}>
                    Test {idx}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <a
          href="#"
          className="font-size-12 text-primary"
          onClick={(e) => {
            e.preventDefault()
            if (onViewChainAnalysis) onViewChainAnalysis(null)
          }}
        >
          View chain analysis →
        </a>
      </div>

      {visible.length === 0 ? (
        <p className="text-muted mb-0">
          {attempts.length === 0
            ? "No diagnostic attempts yet."
            : "No attempts in the selected session."}
        </p>
      ) : (
        <div className="table-responsive">
          <table className="table table-borderless align-middle mb-0">
            <thead>
              <tr className="text-uppercase text-muted fw-bold font-size-12">
                <th style={{ width: "8%" }}>Year</th>
                <th style={{ width: "26%" }}>Topic</th>
                <th style={{ width: "14%" }}>Date</th>
                <th style={{ width: "18%" }}>Score</th>
                <th style={{ width: "16%" }}>Status</th>
                <th style={{ width: "18%" }} className="text-end">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const pills = statusPillsFor(row)
                return (
                  <tr key={row.pk_session_topic_key}>
                    <td className="font-size-13">{row.level_name || "—"}</td>
                    <td className="fw-semibold">{plainText(row.topic_name)}</td>
                    <td className="font-size-13">
                      {formatDate(row.attempted_on)}
                    </td>
                    <td>
                      <ScoreCircles
                        correct={row.correct_count}
                        total={row.total_count}
                      />
                    </td>
                    <td>
                      <div className="d-flex flex-column align-items-start gap-1">
                        {pills.map((p) => (
                          <span
                            key={p.label}
                            className={`badge ${p.bgClass} ${p.textClass} font-size-11`}
                          >
                            {p.label}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="text-end">
                      <div
                        className="d-inline-flex gap-2 justify-content-end flex-wrap"
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => onPreview && onPreview(row)}
                        >
                          <i className="fas fa-eye me-1"></i>
                          Preview
                        </button>
                        {onViewChainAnalysis && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => onViewChainAnalysis(row)}
                            title="View chain analysis for this topic"
                          >
                            <i className="fas fa-link me-1"></i>
                            Chain
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default BmdAttemptHistoryTable
